import { Field } from "formik";
import { useEffect } from "react";
import {
  FUEL_TYPE,
  SAME_TARIFF,
  SUPPLIER_FUEL_TYPE,
  elecMeterTypeOptions,
  gasMeterTypeOptions,
  paymentTypeOptions,
} from "../../constants/tariffInfo";
import { useTariff } from "../../context/TariffContext";
import EN from "../../locales/en";
import RippleLoader from "../RippleLoader";
import Select from "../Select";
import ErrorIcon from "../icons/ErrorIcon";

const EditSharedSupplierTariffFields = ({
  formik,
  filterTariffs,
  filterSuppliers,
}) => {
  const {
    isTariffDataLoading,
    isSupplierDataLoading,
    setSelectedSuppliers,
    setSelectedMeterTypes,
    setSelectedPaymentMethods,
    selectedMeterTypes,
    selectedPaymentMethods,
  } = useTariff();
  const electricityTariff = formik.values.tariffs?.[0];
  const gasTariff = formik.values.tariffs?.[1];
  const electricityTariffErrors = formik.errors.tariffs?.[0];
  const gasTariffErrors = formik.errors.tariffs?.[1];

  const isAllInfoForSupplierFilled =
    electricityTariff?.meterType &&
    electricityTariff?.paymentMethod &&
    gasTariff?.meterType &&
    gasTariff?.paymentMethod;

  const isAllInfoForTariffFilled =
    isAllInfoForSupplierFilled &&
    electricityTariff?.supplierName &&
    gasTariff?.supplierName;

  // if the form values change, check if the selected supplier and tariff are valid. If not, clear them from the form.
  useEffect(() => {
    if (isTariffDataLoading || isSupplierDataLoading) return;
    const sharedSupplierOptions = filterSuppliers(
      FUEL_TYPE.ELECGAS,
      electricityTariff?.meterType,
      electricityTariff?.paymentMethod
    );
    if (
      !sharedSupplierOptions
        .map((supplier) => supplier.value)
        .includes(electricityTariff?.supplierName)
    ) {
      formik.setFieldValue("tariffs.0.supplierName", "");
      formik.setFieldValue("tariffs.1.supplierName", "");
      formik.setFieldValue("tariffs.0.tariffName", "");
      formik.setFieldValue("tariffs.1.tariffName", "");
      setSelectedSuppliers({
        [FUEL_TYPE.ELECGAS]: "",
      });
      return;
    }
    if (formik.values.sameTariff === SAME_TARIFF.YES) {
      // force the gas supplier and tariff to the same as elec
      formik.setFieldValue(
        "tariffs.1.tariffName",
        electricityTariff?.tariffName
      );
      formik.setFieldValue(
        "tariffs.1.supplierName",
        electricityTariff?.supplierName
      );
      const sharedTariffOptions = filterTariffs(
        FUEL_TYPE.ELECGAS,
        electricityTariff?.meterType,
        electricityTariff?.paymentMethod,
        electricityTariff?.supplierName
      );
      if (
        !sharedTariffOptions
          .map((tariff) => tariff.value)
          .includes(electricityTariff?.tariffName)
      ) {
        formik.setFieldValue("tariffs.0.tariffName", "");
        formik.setFieldValue("tariffs.1.tariffName", "");
      }
    } else {
      const elecTariffOptions = filterTariffs(
        FUEL_TYPE.ELEC,
        electricityTariff?.meterType,
        electricityTariff?.paymentMethod,
        electricityTariff?.supplierName
      );
      if (
        !elecTariffOptions
          .map((tariff) => tariff.value)
          .includes(electricityTariff?.tariffName)
      ) {
        formik.setFieldValue("tariffs.0.tariffName", "");
      }
      const gasTariffOptions = filterTariffs(
        FUEL_TYPE.GAS,
        gasTariff?.meterType,
        gasTariff?.paymentMethod,
        gasTariff?.supplierName
      );
      if (
        !gasTariffOptions
          .map((tariff) => tariff.value)
          .includes(gasTariff?.tariffName)
      ) {
        formik.setFieldValue("tariffs.1.tariffName", "");
      }
    }
  }, [formik.values, isTariffDataLoading, isSupplierDataLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className="border-t border-[#afb6b9] pt-6">
        <h3 className="text-[22px] font-bold">
          {EN.edit_tariff_form.meters.title}
        </h3>
        <div className="mt-6 space-y-6">
          <Field
            as={Select}
            id="electricity-meter-select"
            labelText={EN.edit_tariff_form.meters.electricity.label}
            options={elecMeterTypeOptions}
            value={electricityTariff?.meterType || ""}
            keyName="label"
            onChange={(e) => {
              formik.setFieldValue("tariffs.0.meterType", e.target.value);
              setSelectedMeterTypes({
                ...selectedMeterTypes,
                [SUPPLIER_FUEL_TYPE.ELEC]: e.target.value,
              });
            }}
            hasErrors={electricityTariffErrors?.meterType}
            disabled={formik.isSubmitting}
          />

          <Field
            as={Select}
            id="gas-meter-select"
            labelText={EN.edit_tariff_form.meters.gas.label}
            options={gasMeterTypeOptions}
            value={gasTariff?.meterType || ""}
            keyName="label"
            onChange={(e) => {
              formik.setFieldValue("tariffs.1.meterType", e.target.value);
              setSelectedMeterTypes({
                ...selectedMeterTypes,
                [SUPPLIER_FUEL_TYPE.GAS]: e.target.value,
              });
            }}
            hasErrors={gasTariffErrors?.meterType}
            disabled={formik.isSubmitting}
          />
        </div>
      </div>

      <div className="border-t border-[#afb6b9] pt-6">
        <h3 className="text-[22px] font-bold">
          {EN.edit_tariff_form.payment.title}
        </h3>
        <div className="mt-4">
          <Field
            as={Select}
            id="payment-type"
            options={paymentTypeOptions}
            value={
              electricityTariff?.paymentMethod || gasTariff?.paymentMethod || ""
            }
            onChange={(e) => {
              formik.setFieldValue("tariffs.0.paymentMethod", e.target.value);
              formik.setFieldValue("tariffs.1.paymentMethod", e.target.value);
              setSelectedPaymentMethods({
                ...selectedPaymentMethods,
                [SUPPLIER_FUEL_TYPE.ELEC]: e.target.value,
                [SUPPLIER_FUEL_TYPE.GAS]: e.target.value,
              });
            }}
            hasErrors={
              electricityTariffErrors?.paymentMethod ||
              gasTariffErrors?.paymentMethod
            }
            disabled={formik.isSubmitting}
          />
        </div>
      </div>

      {isAllInfoForSupplierFilled && (
        <div className="border-t border-[#afb6b9] pt-6">
          <h3 className="text-[22px] font-bold">
            {EN.edit_tariff_form.supplier.title}
          </h3>
          {isSupplierDataLoading ? (
            <div className="mt-4">
              <RippleLoader />
            </div>
          ) : (
            <div className="mt-4">
              <Field
                as={Select}
                id="common-supplier"
                options={filterSuppliers(
                  FUEL_TYPE.ELECGAS,
                  electricityTariff?.meterType || gasTariff?.meterType,
                  electricityTariff?.paymentMethod || gasTariff?.paymentMethod
                )}
                value={
                  electricityTariff?.supplierName ||
                  gasTariff?.supplierName ||
                  ""
                }
                onChange={(e) => {
                  formik.setFieldValue(
                    "tariffs.0.supplierName",
                    e.target.value
                  );
                  formik.setFieldValue(
                    "tariffs.1.supplierName",
                    e.target.value
                  );
                  setSelectedSuppliers({
                    [FUEL_TYPE.ELECGAS]: e.target.value,
                  });
                }}
                hasErrors={
                  electricityTariffErrors?.supplierName ||
                  gasTariffErrors?.supplierName
                }
                disabled={formik.isSubmitting}
              />
            </div>
          )}
        </div>
      )}

      {isAllInfoForTariffFilled && (
        <div className="border-t border-[#afb6b9] pt-6">
          <h3 className="text-[22px] font-bold">
            {EN.edit_tariff_form.tariff.same_tariff.title}
          </h3>

          <div className="mt-4">
            <Field
              as={Select}
              id="same-tariff-select"
              options={[
                {
                  value: SAME_TARIFF.YES,
                  label: EN.edit_tariff_form.tariff.same_tariff.options.yes,
                },
                {
                  value: SAME_TARIFF.NO,
                  label: EN.edit_tariff_form.tariff.same_tariff.options.no,
                },
              ]}
              value={formik.values.sameTariff}
              onChange={(e) => {
                formik.setFieldValue("sameTariff", e.target.value);
              }}
              hasErrors={formik.errors.sameTariff}
              disabled={formik.isSubmitting}
            />
          </div>
        </div>
      )}

      {isAllInfoForTariffFilled &&
        formik.values.sameTariff &&
        formik.values.sameTariff === SAME_TARIFF.YES && (
          <>
            {isTariffDataLoading ? (
              <div className="mt-4">
                <RippleLoader />
              </div>
            ) : (
              <div className="space-y-2">
                <Field
                  as={Select}
                  id="common-tariff"
                  labelText={EN.edit_tariff_form.tariff.common.title}
                  options={filterTariffs(
                    FUEL_TYPE.ELECGAS,
                    electricityTariff?.meterType,
                    electricityTariff?.paymentMethod,
                    electricityTariff?.supplierName
                  )}
                  keyName="id"
                  value={
                    electricityTariff?.tariffName || gasTariff?.tariffName || ""
                  }
                  onChange={(e) => {
                    formik.setFieldValue(
                      "tariffs.0.tariffName",
                      e.target.value
                    );
                    formik.setFieldValue(
                      "tariffs.1.tariffName",
                      e.target.value
                    );
                  }}
                  hasErrors={
                    electricityTariffErrors?.tariffName ||
                    gasTariffErrors?.tariffName
                  }
                  disabled={formik.isSubmitting}
                />
                {!filterTariffs(
                  FUEL_TYPE.ELECGAS,
                  electricityTariff?.meterType,
                  electricityTariff?.paymentMethod,
                  electricityTariff?.supplierName
                )?.length && (
                  <div className="text-red-500 flex items-center">
                    <ErrorIcon className="mr-2" />
                    No tariffs found
                  </div>
                )}
              </div>
            )}
          </>
        )}

      {isAllInfoForTariffFilled &&
        formik.values.sameTariff &&
        formik.values.sameTariff === SAME_TARIFF.NO && (
          <>
            {isTariffDataLoading ? (
              <div className="mt-4">
                <RippleLoader />
              </div>
            ) : (
              <>
                <div className="space-y-2">
                  <Field
                    as={Select}
                    id="electricity-tariff"
                    labelText={EN.edit_tariff_form.tariff.electricity.title}
                    options={filterTariffs(
                      FUEL_TYPE.ELEC,
                      electricityTariff?.meterType,
                      electricityTariff?.paymentMethod,
                      electricityTariff?.supplierName
                    )}
                    keyName="id"
                    value={electricityTariff?.tariffName || ""}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "tariffs.0.tariffName",
                        e.target.value
                      )
                    }
                    hasErrors={electricityTariffErrors?.tariffName}
                    disabled={formik.isSubmitting}
                  />
                  {!filterTariffs(
                    FUEL_TYPE.ELEC,
                    electricityTariff?.meterType,
                    electricityTariff?.paymentMethod,
                    electricityTariff?.supplierName
                  )?.length && (
                    <div className="text-red-500 flex items-center">
                      <ErrorIcon className="mr-2" />
                      No tariffs found
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Field
                    as={Select}
                    id="gas-tariff"
                    name="gasTariffSelect"
                    labelText={EN.edit_tariff_form.tariff.gas.title}
                    options={filterTariffs(
                      FUEL_TYPE.GAS,
                      gasTariff?.meterType,
                      gasTariff?.paymentMethod,
                      gasTariff?.supplierName
                    )}
                    keyName="id"
                    value={gasTariff?.tariffName || ""}
                    onChange={(e) =>
                      formik.setFieldValue(
                        "tariffs.1.tariffName",
                        e.target.value
                      )
                    }
                    hasErrors={gasTariffErrors?.tariffName}
                    disabled={formik.isSubmitting}
                  />
                  {!filterTariffs(
                    FUEL_TYPE.GAS,
                    gasTariff?.meterType,
                    gasTariff?.paymentMethod,
                    gasTariff?.supplierName
                  )?.length && (
                    <div className="text-red-500 flex items-center">
                      <ErrorIcon className="mr-2" />
                      No tariffs found
                    </div>
                  )}
                </div>
              </>
            )}
          </>
        )}
    </>
  );
};

export default EditSharedSupplierTariffFields;
